import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { CancelledError, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  marketplaceBrowseQueryKey,
  marketplaceBrowseQueryOptions,
  type MarketplaceBrowseVariables,
} from 'components/marketplace/browse/marketplaceBrowse';

import { DEFAULT_LISTING_SORT_SLUG, type ListingSortSlug } from './sortOptions';

export type SearchStatus = 'applied' | 'failed' | 'superseded';

export const useMarketplaceSearch = (
  initialVariables: MarketplaceBrowseVariables,
  initialSort: ListingSortSlug = DEFAULT_LISTING_SORT_SLUG,
) => {
  const queryClient = useQueryClient();
  const [variables, setVariables] = useState(initialVariables);
  // The sort the on-screen results were actually fetched with. Committed only
  // alongside `variables`, so it never advertises an ordering that a cancelled
  // or failed search merely requested.
  const [appliedSort, setAppliedSort] = useState<ListingSortSlug>(initialSort);
  const [isSearching, setIsSearching] = useState(false);
  const [errorId, setErrorId] = useState<number | null>(null);
  const latestErrorId = useRef(0);
  const latestRequestId = useRef(0);
  const query = useInfiniteQuery(marketplaceBrowseQueryOptions({ variables }));
  const { fetchNextPage, hasNextPage } = query;

  const pages = query.data?.pages;
  const firstPage = pages?.[0];
  const mapMarkers = firstPage?.kind === 'initial' ? firstPage.mapMarkers : undefined;
  const listings = useMemo(() => {
    if (!firstPage || !pages) {
      return undefined;
    }
    const lastPage = pages.at(-1) ?? firstPage;
    return {
      ...lastPage.listings,
      totalCount: firstPage.listings.totalCount,
      listings: pages.flatMap((page) => page.listings.listings),
    };
  }, [firstPage, pages]);

  const clearError = useCallback(() => setErrorId(null), []);

  useEffect(() => {
    // Viewport keys can retain thousands of markers, so keep only the active browse result.
    queryClient.removeQueries({ queryKey: marketplaceBrowseQueryKey, type: 'inactive' });
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing
  }, [queryClient, variables]);

  const runSearch = useCallback(
    async (
      nextVariables: MarketplaceBrowseVariables,
      nextSort?: ListingSortSlug,
    ): Promise<SearchStatus> => {
      const requestId = (latestRequestId.current += 1);
      setIsSearching(true);
      setErrorId(null);
      await queryClient.cancelQueries({ queryKey: marketplaceBrowseQueryKey });

      try {
        // oxlint-disable-next-line typescript/no-deprecated -- The data layer remains on the current query-client API until its version migration
        await queryClient.fetchInfiniteQuery({
          ...marketplaceBrowseQueryOptions({ variables: nextVariables }),
          pages: 1,
        });
        if (requestId !== latestRequestId.current) {
          return 'superseded';
        }
        setVariables(nextVariables);
        if (nextSort != null) {
          setAppliedSort(nextSort);
        }
        return 'applied';
      } catch (error) {
        if (requestId !== latestRequestId.current || error instanceof CancelledError) {
          return 'superseded';
        }
        Sentry.captureException(error, {
          tags: { feature: 'marketplace-browse', operation: 'search' },
        });
        setErrorId((latestErrorId.current += 1));
        return 'failed';
      } finally {
        if (requestId === latestRequestId.current) {
          setIsSearching(false);
        }
      }
    },
    [queryClient],
  );

  const loadNextPage = useCallback(async (): Promise<boolean> => {
    if (!hasNextPage) {
      return false;
    }
    try {
      await fetchNextPage({ throwOnError: true });
      setErrorId(null);
      return true;
    } catch (error) {
      if (!(error instanceof CancelledError)) {
        Sentry.captureException(error, {
          tags: { feature: 'marketplace-browse', operation: 'loadNextPage' },
        });
        setErrorId((latestErrorId.current += 1));
      }
      return false;
    }
  }, [fetchNextPage, hasNextPage]);

  return {
    mapMarkers,
    listings,
    appliedSort,
    // Derived from committed `variables` rather than tracked separately, so it
    // can only ever name a page whose results are the ones on screen.
    appliedPage: (variables.listInput.page ?? 0) + 1,
    // Same derivation: the inventory segment of the results on screen, not the
    // one a pending search just requested.
    appliedAvailableSoon: variables.listInput.filters?.availableSoon === true,
    isLoading: isSearching || query.isPending,
    isLoadingMore: query.isFetchingNextPage,
    hasError: errorId !== null,
    errorId,
    clearError,
    runSearch,
    loadNextPage,
  };
};
